import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';

// Never fall back to a shared/test key. An unconfigured environment should
// disable card entry instead of sending card details to the wrong account.
const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = publishableKey ? loadStripe(publishableKey) : Promise.resolve(null);

interface StripeContextType {
  stripe: Stripe | null;
  isReady: boolean;
}

const StripeContext = createContext<StripeContextType>({
  stripe: null,
  isReady: false,
});

export const useStripeContext = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripeContext must be used within a StripeProvider');
  }
  return context;
};

export const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const initStripe = async () => {
      try {
        const stripeInstance = await stripePromise;
        if (mounted && stripeInstance) {
          setStripe(stripeInstance);
          setIsReady(true);
        }
      } catch (error) {
        console.error('Failed to load Stripe:', error);
      }
    };

    initStripe();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <StripeContext.Provider value={{ stripe, isReady }}>
      {children}
    </StripeContext.Provider>
  );
};

export { stripePromise };
